// TAPINTO® Editor Frontend - Hero Component
// Version: 1.0
// Date: 2025-10-01

import React from 'react';
import { useNode } from '@craftjs/core';
import { Button } from '../../common/Button';
import { ComponentProps } from '../../../types';

interface HeroProps extends ComponentProps {
  title: string;
  subtitle: string;
  buttonText: string;
  buttonLink: string;
  backgroundImage?: string;
  backgroundColor: string;
  textColor: string;
}

export function Hero({
  title,
  subtitle,
  buttonText,
  buttonLink,
  backgroundImage,
  backgroundColor,
  textColor,
  ...props
}: HeroProps) {
  const {
    connectors: { connect, drag },
    isActive,
    isHovered,
  } = useNode((state) => ({
    isActive: state.events.selected,
    isHovered: state.events.hovered,
  }));

  return (
    <div
      ref={(ref) => connect(drag(ref))}
      className={`
        relative min-h-[400px] flex items-center justify-center
        ${isActive ? 'ring-2 ring-primary-500' : ''}
        ${isHovered ? 'ring-2 ring-primary-300' : ''}
      `}
      style={{
        backgroundColor,
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      {...props}
    >
      {/* Overlay */}
      {backgroundImage && (
        <div className="absolute inset-0 bg-black bg-opacity-40" />
      )}
      
      {/* Content */}
      <div className="relative z-10 text-center max-w-4xl mx-auto px-4">
        <h1 
          className="text-4xl md:text-6xl font-bold mb-6"
          style={{ color: textColor }}
        >
          {title}
        </h1>
        <p 
          className="text-xl md:text-2xl mb-8"
          style={{ color: textColor }}
        >
          {subtitle}
        </p>
        {buttonText && (
          <Button
            className="bg-accent-500 hover:bg-accent-600 text-white"
            onClick={() => buttonLink && window.open(buttonLink, '_blank')}
          >
            {buttonText}
          </Button>
        )}
      </div>
    </div>
  );
}

Hero.craft = {
  displayName: 'Hero Section',
  props: {
    title: 'Welcome to TAPINTO®',
    subtitle: 'Create amazing pages with our visual editor',
    buttonText: 'Get Started',
    buttonLink: '#',
    backgroundColor: '#1B1464',
    textColor: '#FFFFFF',
  },
  rules: {
    canDrag: () => true,
    canMoveIn: () => true,
    canMoveOut: () => true,
  },
};
